import React, { useEffect, useState, useCallback } from "react";
import { Table, Form, ButtonGroup, ToggleButton, Button, Badge, Spinner } from "react-bootstrap";
import AppPagination from "../../Components/AppPagination";
import { getConfirmedOrders, updateBulkOrder, updateOrderStatus } from "../../Services/orderService";
import { Link } from "react-router-dom";
import PrintLoadSheet from "../../Components/Print/PrintLoadSheet";

const AllOrders = () => {
    const [orders, setOrders] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [selectedOrders, setSelectedOrders] = useState([]);
    const [bulkStatus, setBulkStatus] = useState("pending");
    const [loading, setLoading] = useState(false);

    const [filters, setFilters] = useState({
        status: "all",
        from: "",
        to: "",
        trackingId: ""
    });

    const itemsPerPage = 50;

    // ✅ DATE FORMAT FIX
    const formatDateForAPI = (date, isEnd = false) => {
        if (!date) return undefined;

        const d = new Date(date);

        if (isEnd) {
            d.setHours(23, 59, 59, 999); // end of day fix
        } else {
            d.setHours(0, 0, 0, 0);
        }

        return d.toISOString();
    };

    const fetchOrders = useCallback(async () => {
        try {
            setLoading(true);

            const params = {
                page,
                limit: itemsPerPage, // frontend se limit
                status: filters.status !== "all" ? filters.status : undefined,
                from: formatDateForAPI(filters.from),
                to: formatDateForAPI(filters.to, true),
                trackingId: filters.trackingId || undefined
            };

            const res = await getConfirmedOrders(params);

            if (res?.status === "success") {
                setOrders(res.data || []);
                setTotalPages(res.totalPages || 1);
            } else {
                setOrders([]);
            }

        } catch (err) {
            console.error("Order fetch error:", err);
            setOrders([]);
        } finally {
            setLoading(false);
        }
    }, [page, filters]);

    // ✅ Proper debounce
    useEffect(() => {
        const delay = setTimeout(() => {
            fetchOrders();
        }, 400);

        return () => clearTimeout(delay);
    }, [fetchOrders]);

    // Handlers
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setPage(1);
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleStatusFilter = (value) => {
        setPage(1);
        setFilters(prev => ({ ...prev, status: value }));
    };

    const handleStatusChange = async (id, status) => {
        const res = await updateOrderStatus(id, status);
        if (res?.status === "success") {
            setOrders(prev =>
                prev.map(o => o.id === id ? { ...o, status } : o)
            );
        }
    };

    const handleSelectRow = (id) => {
        setSelectedOrders(prev =>
            prev.includes(id)
                ? prev.filter(i => i !== id)
                : [...prev, id]
        );
    };

    const handleSelectAll = () => {
        const currentIds = orders.map(o => o.id);

        const allSelected = currentIds.every(id =>
            selectedOrders.includes(id)
        );

        if (allSelected) {
            setSelectedOrders(prev =>
                prev.filter(id => !currentIds.includes(id))
            );
        } else {
            setSelectedOrders(prev => [
                ...new Set([...prev, ...currentIds])
            ]);
        }
    };
    const handleBulkUpdate = async () => {

        if (!bulkStatus) {
            return;
        }

        if (!selectedOrders.length) {
            return;
        }

        try {
            setLoading(true);

            const body = {
                trackingId: selectedOrders,
                status: bulkStatus
            };

            const response = await updateBulkOrder(body);

            console.log("API Response:", response);

            if (response?.success) {

                // UI update without refetch
                setOrders(prev =>
                    prev.map(o =>
                        selectedOrders.includes(o.id)
                            ? { ...o, status: bulkStatus }
                            : o
                    )
                );

                setSelectedOrders([]);
                setBulkStatus("pending");

            } else {
                console.error(response?.message || "Bulk update failed");
            }

        } catch (error) {
            console.error("Bulk update failed:", error);
        } finally {
            setLoading(false);
        }
    };




    const getBadge = (status) => {
        if (status === "pending") return "primary";
        if (status === "delivered") return "success";
        if (status === "returned") return "danger";
        return "secondary";
    };

    return (
        <div className="container pb-5">
            <div className="row">

                <div className="col">
                    <h4 className="mb-3">Update Orders</h4>
                </div>


                <div className="col-auto">
                    <Link to="/portal/bulk-update" className="btn btn-dark">
                        Bulk Update
                    </Link>
                </div>
            </div>

            {selectedOrders.length > 0 && (
                <div className="d-flex gap-2 mb-3">
                    <Form.Select
                        style={{ width: 200 }}
                        value={bulkStatus}
                        onChange={(e) => setBulkStatus(e.target.value)}
                    >
                        <option value="pending">Pending</option>
                        <option value="delivered">Delivered</option>
                        <option value="returned">Returned</option>
                    </Form.Select>

                    <Button variant="dark" onClick={handleBulkUpdate}>
                        Update Selected ({selectedOrders.length})
                    </Button>
                </div>
            )}

            {/* Filters */}
            <div className="row g-2 mb-3 align-items-end justify-content-end">
                <div className="col-auto">
                    <Form.Label>From</Form.Label>
                    <Form.Control
                        type="date"
                        name="from"
                        value={filters.from}
                        onChange={handleFilterChange}
                    />
                </div>

                <div className="col-auto">
                    <Form.Label>To</Form.Label>
                    <Form.Control
                        type="date"
                        name="to"
                        value={filters.to}
                        onChange={handleFilterChange}
                    />
                </div>

                <div className="col-auto">
                    <Form.Label>Status</Form.Label><br />
                    <ButtonGroup>
                        {["all", "pending", "delivered", "returned"].map(s => (
                            <ToggleButton
                                key={s}
                                type="radio"
                                variant="outline-dark"
                                className="text-capitalize"
                                checked={filters.status === s}
                                onClick={() => handleStatusFilter(s)}
                            >
                                {s}
                            </ToggleButton>
                        ))}
                    </ButtonGroup>
                </div>

                <div className="col-auto">
                    <Form.Label>Search</Form.Label>
                    <Form.Control
                        type="text"
                        name="trackingId"
                        placeholder="Search Tracking Id"
                        value={filters.trackingId}
                        onChange={handleFilterChange}
                    />
                </div>


                <div className="col-auto ">
                    <PrintLoadSheet title={"Pending Orders"} orders={orders} itemsPerPage={itemsPerPage} page={page} />
                </div>
            </div>

            <Table bordered responsive>
                <thead>
                    <tr>
                        <th>
                            <Form.Check
                                type="checkbox"
                                checked={
                                    orders.length > 0 &&
                                    orders.every(o => selectedOrders.includes(o.id))
                                }
                                onChange={handleSelectAll}
                            />
                        </th>
                        <th>#</th>
                        <th>Order ID</th>
                        <th>VPP</th>
                        <th>Name</th>
                        <th>Contact</th>
                        <th>City</th>
                        <th>Product</th>
                        <th>Qty</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Update</th>
                    </tr>
                </thead>

                <tbody>
                    {loading ? (
                        <tr>
                            <td colSpan="12" className="text-center">
                                <Spinner animation="border" size="sm" /> Loading...
                            </td>
                        </tr>
                    ) : orders.length ? (
                        orders.map((o, i) => (
                            <tr key={o.id}>
                                <td>
                                    <Form.Check
                                        type="checkbox"
                                        checked={selectedOrders.includes(o.id)}
                                        onChange={() => handleSelectRow(o.id)}
                                    />
                                </td>
                                <td>{i + 1 + (page - 1) * itemsPerPage}</td>
                                <td>{o.trackingId}</td>
                                <td>{o.VppNumber || "-"}</td>
                                <td>{o.customerName}</td>
                                <td>{o.phoneNumber}</td>
                                <td>{o.city}</td>
                                <td>{o.product || "-"}</td>
                                <td>{o.quantity}</td>
                                <td>{o.amount}</td>
                                <td>
                                    <Badge bg={getBadge(o.status)}>
                                        {o.status}
                                    </Badge>
                                </td>
                                <td>
                                    <Form.Select
                                        size="sm"
                                        value={o.status}
                                        onChange={(e) =>
                                            handleStatusChange(o.id, e.target.value)
                                        }
                                    >
                                        <option value="pending">Pending</option>
                                        <option value="delivered">Delivered</option>
                                        <option value="returned">Returned</option>
                                    </Form.Select>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="12" className="text-center">
                                No orders found
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>

            <AppPagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
            />
        </div>
    );
};

export default AllOrders;